import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const toastStyles = {
  success: {
    bg: 'bg-gradient-to-r from-emerald-500/90 to-teal-500/90',
    border: 'border-emerald-400/50',
    icon: CheckCircle,
    iconColor: 'text-emerald-100'
  },
  error: {
    bg: 'bg-gradient-to-r from-red-500/90 to-rose-500/90',
    border: 'border-red-400/50',
    icon: XCircle,
    iconColor: 'text-red-100'
  },
  warning: {
    bg: 'bg-gradient-to-r from-amber-500/90 to-orange-500/90',
    border: 'border-amber-400/50',
    icon: AlertCircle,
    iconColor: 'text-amber-100'
  },
  info: {
    bg: 'bg-gradient-to-r from-blue-500/90 to-indigo-500/90',
    border: 'border-blue-400/50',
    icon: Info,
    iconColor: 'text-blue-100'
  }
};

const Toast = ({ id, message, type = 'info', onClose }) => {
  const style = toastStyles[type] || toastStyles.info;
  const Icon = style.icon;

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-xl backdrop-blur-xl ${style.bg} border ${style.border} shadow-2xl min-w-[320px] max-w-[420px] animate-slideIn`}
    >
      <Icon className={`w-5 h-5 ${style.iconColor} flex-shrink-0 mt-0.5`} />
      <p className="text-sm text-white font-medium flex-1">{message}</p>
      <button
        onClick={() => onClose(id)}
        className="text-white/70 hover:text-white transition-colors flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const toast = {
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration),
    warning: (message, duration) => addToast(message, 'warning', duration),
    info: (message, duration) => addToast(message, 'info', duration),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            id={t.id}
            message={t.message}
            type={t.type}
            onClose={removeToast}
          />
        ))}
      </div>

      <style>{`
                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                .animate-slideIn {
                    animation: slideIn 0.3s ease-out;
                }
            `}</style>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
